/**
 * Admin -> Edge Fleet
 *
 * Every edge agent the tenant can see - laptops, server-side agents and
 * gateways - in ONE health vocabulary (release 3.1, fleet wave 1): Healthy,
 * Degraded, Silent, Down, Pending, Retired (lib/edge-health.ts).
 *
 * Release 3.1 moved the table onto GET /v1/edge/devices. The old page read the
 * 200 most recently seen pods and filtered the rest in the browser, so at
 * 13,000 laptops an operator could not find the other 12,800. Now search,
 * filters, sort and paging all run on the server over the whole fleet, the
 * state tiles count every device that matches (not the page), and Export CSV
 * streams every matching device in the same order.
 *
 * Release 3.1 wave 2a: every agent type in one registry. The table names each
 * row's agent type (laptop agent, server scanner, DAM collector, decision
 * gateway, DSAR actuation connector, agentless collection) and filters by it
 * on the server (`agentType`); the agentless collection panel (caps, runs in
 * flight, recent runs) sits under the rollout controls.
 *
 * Backend:
 *   GET /v1/edge/devices        -> { devices, nextCursor, summary, matching }
 *   GET /v1/edge/devices/export -> text/csv (same filters and sort)
 */
import { useRegulationText } from '@/lib/regulation-terms';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Server,
  Plus,
  Activity as ActivityIcon,
  ShieldAlert,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ArrowDown,
  ArrowUp,
  Loader2,
  WifiOff,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  ApiError,
  exportEdgeDevicesCsv,
  getAgentEntitlement,
  getEdgeFleetVersions,
  listBusinessUnits,
  listEdgeDevices,
  listEdgeInstallTokens,
  type AgentEntitlementVerdict,
  type EdgeAgentClass,
  type EdgeAgentType,
  type EdgeComponent,
  type EdgeDegradedReason,
  type EdgeDeviceFilters,
  type EdgeDeviceSort,
  type EdgeDeviceUpdate,
  type EdgeDeviceUpdateFilter,
  type EdgeFleetSummary,
  type EdgeHealthStatus,
  type EdgePodSummary,
} from '@/lib/api';
import {
  EDGE_AGENT_CLASS_LABEL,
  EDGE_DEGRADED_REASONS,
  EDGE_HEALTH_HINT,
  EDGE_HEALTH_LABEL,
  EDGE_HEALTH_STATES,
  EDGE_HEALTH_VARIANT,
  edgeDegradedReason,
  edgeDegradedReasonOptions,
  edgeHealthHint,
} from '@/lib/edge-health';
import { EDGE_AGENT_TYPES, EDGE_AGENT_TYPE_LABEL, edgeAgentTypeLabel } from '@/lib/edge-agent-types';

import { EdgePodDetailDrawer } from './EdgePodDetailDrawer';
import { BudgetWatchdogPanel } from './BudgetWatchdogPanel';
import { InstallTokenPanel } from './InstallTokenPanel';
import { FleetRolloutControls } from './FleetRolloutControls';
import { AgentlessCollectionPanel } from './AgentlessCollectionPanel';
import { ScannerPolicyPanel } from './ScannerPolicyPanel';
import { DeviceGroupsPanel } from './DeviceGroupsPanel';
import { FullScanIntervalPanel } from './FullScanIntervalPanel';
import { UpdateRolloutsPanel } from './UpdateRolloutsPanel';
import { AgentRemovalPanel } from './AgentRemovalPanel';

// The self-measurement formatters moved to their own module (the drawer uses
// them too); re-exported so existing imports keep working.
export { cpuTitle, formatCpu, formatRss } from './edge-measurement-format';

const COMPONENT_LABELS: Record<EdgeComponent, string> = {
  discovery: 'Discovery',
  activity: 'Activity',
  detection: 'Detection',
};

const COMPONENT_ICON: Record<EdgeComponent, typeof Server> = {
  discovery: Server,
  activity: ActivityIcon,
  detection: ShieldAlert,
};

const HEALTH_ICON: Record<EdgeHealthStatus, typeof CheckCircle2> = {
  healthy: CheckCircle2,
  degraded: AlertTriangle,
  silent: Clock,
  down: WifiOff,
  pending: Clock,
  decommissioned: XCircle,
};

/**
 * How a device's health renders, in the one vocabulary. Indexing the maps
 * directly is what took the whole page down (React error 130) the day the
 * controller started writing a word this build did not know: the lookup
 * returned undefined and was rendered as a component. A state this build does
 * not know still gets a neutral badge and its raw name, so one new value can
 * never blank the fleet again.
 */
export function healthPresentation(status: string): {
  Icon: typeof CheckCircle2;
  variant: 'success' | 'warning' | 'critical' | 'glass';
  label: string;
  hint: string;
} {
  const known = (EDGE_HEALTH_STATES as readonly string[]).includes(status)
    ? (status as EdgeHealthStatus)
    : null;
  if (!known) return { Icon: Clock, variant: 'glass', label: status, hint: 'a state this console does not know' };
  return {
    Icon: HEALTH_ICON[known],
    variant: EDGE_HEALTH_VARIANT[known],
    label: EDGE_HEALTH_LABEL[known],
    hint: EDGE_HEALTH_HINT[known],
  };
}

/** The tiles above the table: the states an operator acts on. */
export const STATE_TILES: EdgeHealthStatus[] = ['healthy', 'degraded', 'silent', 'down', 'pending'];

export const DEVICE_PAGE_SIZE = 50;

/** The server's default order per sort (device-search.ts SORTS). */
export const DEFAULT_ORDER: Record<EdgeDeviceSort, 'asc' | 'desc'> = {
  lastSeen: 'desc',
  name: 'asc',
  hostname: 'asc',
  state: 'asc',
  version: 'desc',
  os: 'asc',
  enrolled: 'desc',
  reason: 'asc',
};

export type LastSeenRange = '' | 'within24h' | '1to7d' | 'over7d' | 'never';

export interface DeviceFilterState {
  /** Release 3.1 wave 2a: one agent type, filtered by the server. */
  agentType: EdgeAgentType | '';
  agentClass: EdgeAgentClass | '';
  component: EdgeComponent | '';
  businessUnitId: string;
  installTokenId: string;
  os: string;
  state: EdgeHealthStatus | '';
  lastSeen: LastSeenRange;
  hasFindings: '' | 'true' | 'false';
  /** Release 3.2 phase 2: where the device's self-update stands. */
  update: EdgeDeviceUpdateFilter | '';
  /** Release 3.3: Degraded for this reason code (the agent's or the controller's). */
  degradedReason: string;
}

export const EMPTY_FILTERS: DeviceFilterState = {
  agentType: '',
  agentClass: '',
  component: '',
  businessUnitId: '',
  installTokenId: '',
  os: '',
  state: '',
  lastSeen: '',
  hasFindings: '',
  update: '',
  degradedReason: '',
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * The API filters for the page's controls. Pure, so it is tested without a
 * browser. "Never checked in" is the Pending state (a device with no check-in
 * matches no last-seen range), so it asks for that state.
 */
export function deviceQueryFor(
  f: DeviceFilterState,
  text: { q: string; version: string },
  sort: EdgeDeviceSort,
  order: 'asc' | 'desc',
  now: number = Date.now(),
): EdgeDeviceFilters {
  const out: EdgeDeviceFilters = { sort, order };
  if (text.q.trim()) out.q = text.q.trim();
  if (text.version.trim()) out.version = text.version.trim();
  if (f.agentType) out.agentType = f.agentType;
  if (f.agentClass) out.agentClass = f.agentClass;
  if (f.component) out.component = f.component;
  if (f.businessUnitId) out.businessUnitId = f.businessUnitId;
  if (f.installTokenId) out.installTokenId = f.installTokenId;
  if (f.os) out.os = f.os;
  if (f.state) out.state = f.state;
  if (f.hasFindings) out.hasFindings = f.hasFindings === 'true';
  if (f.update) out.update = f.update;
  if (f.degradedReason) out.degradedReason = f.degradedReason;
  const iso = (ms: number) => new Date(ms).toISOString();
  switch (f.lastSeen) {
    case 'within24h':
      out.lastSeenAfter = iso(now - DAY_MS);
      break;
    case '1to7d':
      out.lastSeenAfter = iso(now - 7 * DAY_MS);
      out.lastSeenBefore = iso(now - DAY_MS);
      break;
    case 'over7d':
      out.lastSeenBefore = iso(now - 7 * DAY_MS);
      break;
    case 'never':
      out.state = 'pending';
      break;
    default:
      break;
  }
  return out;
}

/** The device list's update filters (release 3.2 phase 2). */
export const UPDATE_FILTER_LABEL: Record<EdgeDeviceUpdateFilter, string> = {
  behind_target: 'Behind the rollout target',
  failed: 'Update failed',
  rolled_back: 'Rolled back',
  managed_externally: 'Managed by your own tool',
};

/**
 * The Update cell: the state the agent reported (with its reason), and the
 * device's stage and result in the latest rollout. Every value is the device's
 * own string, rendered as text.
 */
export function UpdateCell({ update }: { update: EdgeDeviceUpdate | null }) {
  if (!update) return <span className="text-muted-foreground italic">-</span>;
  const bad = update.state === 'failed' || update.state === 'rolled_back';
  return (
    <div className="space-y-0.5">
      {update.state ? (
        <div className={bad ? 'text-red-700 dark:text-red-300' : 'text-foreground'}>
          {update.state.replace(/_/g, ' ')}
          {update.reason ? <span className="text-muted-foreground"> ({update.reason})</span> : null}
        </div>
      ) : null}
      {update.rollout ? (
        <div className="text-[10px] text-muted-foreground">
          {update.rollout.targetVersion}: {update.rollout.stageLabel}, {update.rollout.status.replace(/_/g, ' ')}
        </div>
      ) : null}
    </div>
  );
}

/** "Wave 1a2b3c4d", the way the install token panel names a token. */
export function waveLabel(id: string): string {
  return `Wave ${id.slice(0, 8)}`;
}

const NOT_REPORTED_TEXT = 'not reported';

function checkInAge(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function fmtN(n: number): string {
  return n.toLocaleString('en-IN');
}

/** A value, or a visible "not reported" (never blank, never zero). */
function Reported({ value, mono = false }: { value: string | number | null | undefined; mono?: boolean }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground italic">{NOT_REPORTED_TEXT}</span>;
  }
  return <span className={mono ? 'font-mono text-[11px]' : undefined}>{value}</span>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const SELECT_CLASS = 'glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40';

const EMPTY_SUMMARY: EdgeFleetSummary = {
  total: 0, healthy: 0, degraded: 0, silent: 0, down: 0, pending: 0, decommissioned: 0, stale: 0,
};

/**
 * The Degraded reason cell (release 3.3, R3-OI-184): the reason in plain
 * words, its one-line next step, and the agent's own sentence on hover. A
 * device that is not Degraded shows nothing here.
 */
export function DegradedReasonCell({ reason }: { reason: EdgeDegradedReason | null }): JSX.Element | null {
  if (!reason) return null;
  return (
    <div title={reason.detail ? `The agent says: ${reason.detail}` : undefined}>
      <div className="text-xs font-medium text-foreground">{reason.label}</div>
      <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{reason.nextStep}</div>
      <code className="font-mono text-[10px] text-muted-foreground">{reason.code}</code>
    </div>
  );
}

interface Column {
  key: string;
  label: string;
  sort?: EdgeDeviceSort;
  title?: string;
}

const COLUMNS: Column[] = [
  { key: 'name', label: 'Name', sort: 'name' },
  { key: 'hostname', label: 'Hostname', sort: 'hostname' },
  { key: 'type', label: 'Agent type' },
  { key: 'health', label: 'Health', sort: 'state', title: 'Sorted worst first: Inactive, Offline, Unhealthy, Pending, Healthy' },
  {
    key: 'reason',
    label: 'Why unhealthy',
    sort: 'reason',
    title: 'Why an Unhealthy device is unhealthy, in plain words, with the next step. Sorted A-Z by reason, every other device after them',
  },
  { key: 'lastSeen', label: 'Last check-in', sort: 'lastSeen' },
  { key: 'version', label: 'Version', sort: 'version' },
  { key: 'update', label: 'Update', title: 'Self-update state the agent reported, and its stage in the latest update rollout' },
  { key: 'os', label: 'OS', sort: 'os' },
  { key: 'wave', label: 'Wave' },
  { key: 'bu', label: 'Business unit' },
  { key: 'enrolled', label: 'Enrolled', sort: 'enrolled' },
  { key: 'findings', label: 'Open findings', title: 'Open reportable findings on this laptop' },
];

export function AdminEdgeFleet(): JSX.Element {
  const tx = useRegulationText();
  const navigate = useNavigate();

  // What the operator asked for.
  const [filters, setFilters] = useState<DeviceFilterState>(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [versionText, setVersionText] = useState('');
  const [sort, setSort] = useState<EdgeDeviceSort>('lastSeen');
  const [order, setOrder] = useState<'asc' | 'desc'>(DEFAULT_ORDER.lastSeen);
  const q = useDebounced(search, 300);
  const version = useDebounced(versionText, 300);

  // Paging: a stack of the cursors that opened each page (page 1 = null). A
  // cursor is only valid for the sort and order it came from, and a page of a
  // different filter is a different list, so the stack belongs to one query:
  // when the query changes, the stack starts again at page 1.
  const queryKey = JSON.stringify({ filters, q, version, sort, order });
  const [paging, setPaging] = useState<{ key: string; cursors: Array<string | null> }>({
    key: queryKey,
    cursors: [null],
  });
  const cursors = paging.key === queryKey ? paging.cursors : [null];
  const cursor = cursors[cursors.length - 1] ?? null;
  const pageIndex = cursors.length - 1;

  // What the server answered.
  const [devices, setDevices] = useState<EdgePodSummary[]>([]);
  const [summary, setSummary] = useState<EdgeFleetSummary>(EMPTY_SUMMARY);
  const [matching, setMatching] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [drawerPodId, setDrawerPodId] = useState<string | null>(null);

  // Endpoint class only in the banner: it is the line customers actually
  // negotiate and the one a 20,000-laptop rollout breaches.
  const [entitlement, setEntitlement] = useState<AgentEntitlementVerdict | null>(null);

  // Options for the filter bar. Each is best-effort: a list that cannot be read
  // leaves that filter with "All" only, never blanks the page.
  const [units, setUnits] = useState<Array<{ id: string; name: string }>>([]);
  const [waves, setWaves] = useState<Array<{ id: string; revoked: boolean }>>([]);
  const [versions, setVersions] = useState<string[]>([]);

  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const r = await listEdgeDevices({
        ...deviceQueryFor(filters, { q, version }, sort, order),
        ...(cursor ? { cursor } : {}),
        limit: DEVICE_PAGE_SIZE,
      });
      if (mine !== seq.current) return;
      setDevices(r.devices);
      setSummary(r.summary);
      setMatching(r.matching);
      setNextCursor(r.nextCursor);
      setLoaded(true);
    } catch (e) {
      if (mine !== seq.current) return;
      // A cursor from an older list (the sort changed under it) is refused
      // with 400 INVALID_CURSOR: start again at page 1 rather than strand
      // the operator on an error.
      if (cursor && e instanceof ApiError && e.status === 400) {
        setPaging({ key: queryKey, cursors: [null] });
        setNotice('The list changed since that page was read; showing the first page again.');
        return;
      }
      setError(e instanceof Error ? e.message : 'failed to load devices');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [filters, q, version, sort, order, cursor, queryKey]);

  // Load on every change of the query or page, and refresh the current page
  // every 30 s (the page the operator is on, not page 1).
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    // Best-effort: an entitlement lookup that fails must not blank the fleet
    // view, which is the page an operator opens during an incident.
    getAgentEntitlement()
      .then((e) => { if (!cancelled) setEntitlement(e.classes.endpoint); })
      .catch(() => undefined);
    listBusinessUnits()
      .then((r) => { if (!cancelled) setUnits(r.businessUnits.map((b) => ({ id: b.id, name: b.name }))); })
      .catch(() => undefined);
    listEdgeInstallTokens()
      .then((r) => { if (!cancelled) setWaves(r.tokens.map((t) => ({ id: t.id, revoked: t.revokedAt !== null }))); })
      .catch(() => undefined);
    getEdgeFleetVersions()
      .then((v) => {
        if (!cancelled) setVersions([...new Set(v.counts.map((c) => c.version))].sort().reverse());
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const setFilter = <K extends keyof DeviceFilterState>(key: K, value: DeviceFilterState[K]) => {
    setNotice(null);
    setFilters((f) => {
      const next = { ...f, [key]: value };
      // "Never checked in" IS the Pending state; a state picked afterwards
      // replaces it rather than asking for two contradictory things.
      if (key === 'state' && f.lastSeen === 'never') next.lastSeen = '';
      if (key === 'lastSeen' && value === 'never') next.state = '';
      return next;
    });
  };

  /** The state the list is showing (a tile, or Pending via "never checked in"). */
  const activeState: EdgeHealthStatus | '' = filters.lastSeen === 'never' ? 'pending' : filters.state;

  const onTile = (s: EdgeHealthStatus) => setFilter('state', activeState === s ? '' : s);

  const onSort = (s: EdgeDeviceSort) => {
    setNotice(null);
    if (s === sort) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(s);
      setOrder(DEFAULT_ORDER[s]);
    }
  };

  const filtersActive =
    JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS) || search.trim() !== '' || versionText.trim() !== '';

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch('');
    setVersionText('');
    setNotice(null);
  };

  const goNext = () => {
    if (!nextCursor) return;
    setPaging({ key: queryKey, cursors: [...cursors, nextCursor] });
  };
  const goPrevious = () => {
    if (cursors.length <= 1) return;
    setPaging({ key: queryKey, cursors: cursors.slice(0, -1) });
  };

  const onExport = async () => {
    setExporting(true);
    setNotice(null);
    try {
      const r = await exportEdgeDevicesCsv(deviceQueryFor(filters, { q, version }, sort, order));
      setNotice(
        r.total === null
          ? `Exported the matching devices to ${r.filename}.`
          : `Exported ${fmtN(r.total)} device${r.total === 1 ? '' : 's'} to ${r.filename}.`,
      );
    } catch (e) {
      setNotice(e instanceof Error ? `Export failed: ${e.message}` : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const unitName = useMemo(() => {
    const m = new Map(units.map((u) => [u.id, u.name]));
    return (d: EdgePodSummary): string | null => {
      if (d.businessUnitId === null) return 'No unit';
      return d.businessUnitName ?? m.get(d.businessUnitId) ?? null;
    };
  }, [units]);

  const from = devices.length === 0 ? 0 : pageIndex * DEVICE_PAGE_SIZE + 1;
  const to = pageIndex * DEVICE_PAGE_SIZE + devices.length;
  const fleetEmpty = loaded && !filtersActive && summary.total === 0 && devices.length === 0;

  return (
    <PageContainer>
      <PageHeader
        title="Agent Management"
        subtitle={tx("Every edge agent you can see, of every type: laptop agents, server scanners, DAM collectors, decision gateways, DSAR actuation connectors and agentless collection. Search, filters and sort run on the server over the whole fleet, and the counts cover every device that matches, not just this page. Refreshes every 30 s.")}
        badges={[
          { label: 'Live - 30 s refresh', variant: 'glass' },
          ...(loading && !loaded
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="glass" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => navigate({ to: '/admin/edge-provisioning' })}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Deploy agent
            </Button>
          </div>
        }
      />

      {error && (
        <ErrorState
          title="Failed to load devices"
          detail={error}
          onRetry={() => void load()}
        />
      )}

      {/*
        Entitlement banner. Shown ONLY when there is something to say - a
        permanent "you are within your limit" strip is noise that trains an
        operator to stop reading this area, which is the one place the real
        warning has to land. It never suggests agents are blocked, because they
        are not: enrolment always continues; only new install tokens are gated.
      */}
      {entitlement && entitlement.state !== 'within' && entitlement.state !== 'unlimited' && (
        <div
          className={`mb-6 rounded-lg border p-4 flex items-start gap-3 ${
            entitlement.state === 'over'
              ? 'border-red-500/40 bg-red-500/10'
              : 'border-amber-500/40 bg-amber-500/10'
          }`}
        >
          <AlertTriangle
            className={`h-5 w-5 mt-0.5 shrink-0 ${
              entitlement.state === 'over' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
            }`}
          />
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {entitlement.state === 'over'
                ? `${entitlement.over} endpoint agent(s) over entitlement`
                : `${entitlement.over} endpoint agent(s) over - inside the grace band`}
            </div>
            <p className="text-xs text-muted-foreground">{entitlement.summary}</p>
            <p className="text-[11px] text-muted-foreground">
              Your agents keep running and new devices still enrol. The agent ceiling is managed by
              FOCTTA - request an increase to issue new install tokens.
            </p>
          </div>
        </div>
      )}

      {/*
        State tiles. Counted by the server over every device that matches the
        other filters, ignoring the state filter itself, so clicking a tile
        narrows the list without zeroing the other tiles.
      */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" role="group" aria-label="Devices by health">
        <button
          type="button"
          onClick={() => setFilter('state', '')}
          aria-pressed={activeState === ''}
          className={`glass-lite rounded-lg border p-3 text-left transition-colors ${
            activeState === '' ? 'border-primary/60' : 'border-border/40 hover:border-border'
          }`}
        >
          <div className="text-[11px] text-muted-foreground">All devices</div>
          <div className="text-2xl font-semibold tabular-nums" data-testid="tile-count-all">{fmtN(summary.total)}</div>
          <div className="text-[10px] text-muted-foreground">in these filters</div>
        </button>
        {STATE_TILES.map((s) => {
          const h = healthPresentation(s);
          const Icon = h.Icon;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onTile(s)}
              aria-pressed={activeState === s}
              aria-label={`${h.label}: ${fmtN(summary[s])} devices`}
              title={h.hint}
              className={`glass-lite rounded-lg border p-3 text-left transition-colors ${
                activeState === s ? 'border-primary/60' : 'border-border/40 hover:border-border'
              }`}
            >
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Icon className="h-3 w-3" />
                {h.label}
              </div>
              <div className="text-2xl font-semibold tabular-nums" data-testid={`tile-count-${s}`}>
                {fmtN(summary[s])}
              </div>
              <div className="text-[10px] leading-tight text-muted-foreground">{h.hint}</div>
            </button>
          );
        })}
      </div>

      <GlassCard className="p-6">
        <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Devices</CardTitle>
            <CardDescription clamp>
              Click a column heading to sort, and a row to open the device.
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="glass"
            onClick={() => void onExport()}
            disabled={exporting}
            title="Every device that matches these filters, in this order, as a CSV file"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            Export CSV
          </Button>
        </CardHeader>

        {/* Filter bar. Every filter is applied by the server. */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="relative">
            <span className="sr-only">Search devices</span>
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setNotice(null); }}
              placeholder="Hostname, name, user, IP, serial or instance id"
              maxLength={100}
              aria-label="Search devices"
              className="w-72 glass-lite text-xs rounded pl-7 pr-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
            />
          </label>
          <select
            aria-label="Agent type"
            value={filters.agentType}
            onChange={(e) => setFilter('agentType', e.target.value as EdgeAgentType | '')}
            className={SELECT_CLASS}
          >
            <option value="">All agent types</option>
            {EDGE_AGENT_TYPES.map((t) => (
              <option key={t} value={t}>{tx(EDGE_AGENT_TYPE_LABEL[t])}</option>
            ))}
          </select>
          <select
            aria-label="Class"
            title="The licence class: laptop, server-side, gateway, or a service that is not metered per device"
            value={filters.agentClass}
            onChange={(e) => setFilter('agentClass', e.target.value as EdgeAgentClass | '')}
            className={SELECT_CLASS}
          >
            <option value="">All classes</option>
            {(Object.keys(EDGE_AGENT_CLASS_LABEL) as EdgeAgentClass[]).map((c) => (
              <option key={c} value={c}>{EDGE_AGENT_CLASS_LABEL[c]}</option>
            ))}
          </select>
          <select
            aria-label="Component"
            value={filters.component}
            onChange={(e) => setFilter('component', e.target.value as EdgeComponent | '')}
            className={SELECT_CLASS}
          >
            <option value="">All components</option>
            {(Object.keys(COMPONENT_LABELS) as EdgeComponent[]).map((c) => (
              <option key={c} value={c}>{COMPONENT_LABELS[c]}</option>
            ))}
          </select>
          <select
            aria-label="Business unit"
            value={filters.businessUnitId}
            onChange={(e) => setFilter('businessUnitId', e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All business units</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <select
            aria-label="Wave"
            value={filters.installTokenId}
            onChange={(e) => setFilter('installTokenId', e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All waves</option>
            {waves.map((w) => (
              <option key={w.id} value={w.id}>{waveLabel(w.id)}{w.revoked ? ' (revoked)' : ''}</option>
            ))}
          </select>
          <input
            type="text"
            list="edge-fleet-versions"
            value={versionText}
            onChange={(e) => { setVersionText(e.target.value); setNotice(null); }}
            placeholder="Version"
            maxLength={32}
            aria-label="Version"
            className="w-24 glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
          />
          <datalist id="edge-fleet-versions">
            {versions.map((v) => <option key={v} value={v} />)}
          </datalist>
          <select
            aria-label="OS"
            value={filters.os}
            onChange={(e) => setFilter('os', e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All OS</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
            <option value="linux">Linux</option>
          </select>
          <select
            aria-label="Health"
            value={activeState}
            onChange={(e) => setFilter('state', e.target.value as EdgeHealthStatus | '')}
            className={SELECT_CLASS}
          >
            <option value="">All health</option>
            {EDGE_HEALTH_STATES.map((s) => (
              <option key={s} value={s}>{EDGE_HEALTH_LABEL[s]}</option>
            ))}
          </select>
          <select
            aria-label="Why unhealthy"
            title="Unhealthy devices with this reason"
            value={filters.degradedReason}
            onChange={(e) => setFilter('degradedReason', e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">Any degraded reason</option>
            {edgeDegradedReasonOptions().map((o) => (
              <option key={o.code} value={o.code}>{o.label}</option>
            ))}
            {filters.degradedReason && !Object.hasOwn(EDGE_DEGRADED_REASONS, filters.degradedReason) && (
              <option value={filters.degradedReason}>{filters.degradedReason.replace(/_/g, ' ')}</option>
            )}
          </select>
          <select
            aria-label="Last check-in"
            value={filters.lastSeen}
            onChange={(e) => setFilter('lastSeen', e.target.value as LastSeenRange)}
            className={SELECT_CLASS}
          >
            <option value="">Any last check-in</option>
            <option value="within24h">Within 24 h</option>
            <option value="1to7d">1 to 7 days ago</option>
            <option value="over7d">Over 7 days ago</option>
            <option value="never">Pending (never checked in)</option>
          </select>
          <select
            aria-label="Update"
            title="Behind the current rollout's target, or the self-update state the agent reported"
            value={filters.update}
            onChange={(e) => setFilter('update', e.target.value as EdgeDeviceUpdateFilter | '')}
            className={SELECT_CLASS}
          >
            <option value="">Any update state</option>
            {(Object.keys(UPDATE_FILTER_LABEL) as EdgeDeviceUpdateFilter[]).map((k) => (
              <option key={k} value={k}>{UPDATE_FILTER_LABEL[k]}</option>
            ))}
          </select>
          <select
            aria-label="Findings"
            value={filters.hasFindings}
            onChange={(e) => setFilter('hasFindings', e.target.value as '' | 'true' | 'false')}
            className={SELECT_CLASS}
          >
            <option value="">Any findings</option>
            <option value="true">Has open findings</option>
            <option value="false">No open findings</option>
          </select>
          {filtersActive && (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        {notice && <div className="mb-2 text-[11px] text-foreground">{notice}</div>}

        {!loaded && loading && <LoadingState caption="Loading devices..." />}

        {fleetEmpty && (
          <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
            <Server className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No edge devices registered</div>
            <div className="mt-1">
              Use the <strong>Deploy agent</strong> action (top-right) to mint an install bundle.
              Once a device checks in, it appears here within 30 s.
            </div>
          </div>
        )}

        {loaded && !fleetEmpty && (
          <>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <span data-testid="showing-line">
                {matching === 0
                  ? 'No device matches these filters.'
                  : `Showing ${fmtN(from)}-${fmtN(to)} of ${fmtN(matching)} matching`}
                {loading ? <Loader2 className="ml-2 inline h-3 w-3 animate-spin" /> : null}
              </span>
              <span className="flex items-center gap-1">
                <Button size="sm" variant="glass" onClick={goPrevious} disabled={pageIndex === 0 || loading}>
                  <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Previous
                </Button>
                <Button size="sm" variant="glass" onClick={goNext} disabled={!nextCursor || loading}>
                  Next <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                </Button>
              </span>
            </div>

            {devices.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs tabular">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/30">
                      {COLUMNS.map((c) => {
                        const active = c.sort !== undefined && c.sort === sort;
                        return (
                          <th
                            key={c.key}
                            className="py-2 pr-3 font-normal whitespace-nowrap"
                            aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                            title={c.title}
                          >
                            {c.sort ? (
                              <button
                                type="button"
                                onClick={() => onSort(c.sort as EdgeDeviceSort)}
                                className={`inline-flex items-center gap-0.5 hover:text-foreground ${active ? 'text-foreground' : ''}`}
                              >
                                {c.label}
                                {active ? (
                                  order === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                ) : null}
                              </button>
                            ) : (
                              c.label
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => {
                      const health = healthPresentation(d.healthStatus);
                      const HealthIcon = health.Icon;
                      const ComponentIcon = COMPONENT_ICON[d.component] ?? Server;
                      const bu = unitName(d);
                      return (
                        <tr
                          key={d.id}
                          className="border-b border-border/15 hover:bg-muted/20 cursor-pointer align-top"
                          onClick={() => setDrawerPodId(d.id)}
                          title={`Open ${d.displayName}`}
                        >
                          <td className="py-2 pr-3">
                            <div className="text-sm font-medium">{d.displayName}</div>
                            <code className="font-mono text-[10px] text-muted-foreground">
                              {d.podId.length > 16 ? `${d.podId.slice(0, 16)}...` : d.podId}
                            </code>
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap"><Reported value={d.hostname} mono /></td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <div data-testid="agent-type-cell">{tx(edgeAgentTypeLabel(d))}</div>
                            <div className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                              <ComponentIcon className="h-3 w-3" />
                              {COMPONENT_LABELS[d.component] ?? d.component}
                            </div>
                          </td>
                          <td className="py-2 pr-3" title={edgeHealthHint(d.healthStatus, d.agentClass, d.healthReason)}>
                            <Badge variant={health.variant} className="text-[10px] inline-flex items-center gap-1 whitespace-nowrap">
                              <HealthIcon className="h-3 w-3" />
                              {health.label}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 min-w-[14rem] max-w-[22rem]" data-testid="reason-cell">
                            <DegradedReasonCell reason={edgeDegradedReason(d)} />
                          </td>
                          <td
                            className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground"
                            title={d.lastHeartbeatAt ? new Date(d.lastHeartbeatAt).toLocaleString() : 'never checked in'}
                          >
                            {checkInAge(d.lastHeartbeatAt)}
                          </td>
                          <td className="py-2 pr-3 font-mono text-[11px]">{d.version}</td>
                          <td className="py-2 pr-3" data-testid="update-cell">
                            <UpdateCell update={d.update ?? null} />
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <Reported value={d.osName ? `${d.osName}${d.osVersion ? ` ${d.osVersion}` : ''}` : null} />
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <Reported value={d.installTokenId ? waveLabel(d.installTokenId) : null} mono />
                          </td>
                          <td className="py-2 pr-3"><Reported value={bu} /></td>
                          <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                            {new Date(d.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-2 pr-3 tabular-nums">
                            {d.openFindings === null || d.openFindings === undefined ? (
                              <Reported value={null} />
                            ) : (
                              <span className={d.openFindings > 0 ? 'text-warning' : 'text-muted-foreground'}>
                                {fmtN(d.openFindings)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {devices.length === 0 && matching === 0 && filtersActive && (
              <div className="glass-lite rounded-md p-6 text-center text-xs text-muted-foreground">
                Nothing matches.{' '}
                <button type="button" className="text-primary hover:underline" onClick={clearFilters}>
                  Clear the filters
                </button>
              </div>
            )}
          </>
        )}
      </GlassCard>

      {/*
        Phase 6. Placed under the fleet table on purpose: doc 34 section 4
        orders the console by what a customer asks, and "which agent is
        misbehaving" comes after "what do I have" but before anything else.
      */}
      <div className="mt-4">
        <BudgetWatchdogPanel />
        <InstallTokenPanel />
        <FleetRolloutControls />
        <DeviceGroupsPanel />
        <FullScanIntervalPanel />
        <UpdateRolloutsPanel />
        <AgentRemovalPanel />
        <AgentlessCollectionPanel />
        <ScannerPolicyPanel />
      </div>

      <EdgePodDetailDrawer
        podId={drawerPodId}
        onClose={() => setDrawerPodId(null)}
        onMutated={() => void load()}
      />
    </PageContainer>
  );
}
